// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasParentProductId, withParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import RelatedProductCard from "@tps/App/Components/Product/RelatedProductCard";
import loadRelatedProductsCarousel from "@tps/App/Handlers/RelatedProductsCarousel/LoadRelatedProductsCarousel";
import { CustomAppState } from "@tps/App/Store/Reducers";
import ProductCarouselProduct, { ProductCarouselProductStyles } from "@tps/Overrides/Components/ProductCarouselProduct";
import { RelatedProductModel } from "@tps/XDL/types";
import { color, xScreen } from "@tps/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    relatedProductType = "relatedProductType",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showAvailability = "showAvailability",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.relatedProductType]: string;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext &
    HasParentProductId;

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps & HasParentProductId) => {
    const products = getProductsForProductInfoList(state, ownProps.id);
    const productInfosById = products
        ? products.reduce((acc, product) => {
            const info = getProductInfoFromList(state, ownProps.id, product.id);
            if (info) {
                acc[product.id] = info;
            }
            return acc;
        }, {} as Record<string, ProductInfo>)
        : {};

    return {
        products,
        errorMessage: getErrorMessage(state, ownProps.id),
        session: state.context.session,
        isTabletScreen: state.xCommon.isTabletScreen,
        variantTraits: getProductState(state, ownProps.parentProductId).value?.variantTraits,
        variantChildren: getVariantChildrenDataView(state, ownProps.parentProductId).value,
        isAuthenticated: state.context.session.isAuthenticated && !state.context.session.isGuest,
        hideItemsOutOfStock: (getSettingsCollection(state).catalogCustomSettings as any).hideItemsOutOfStock,
        productInfosById,
        isLoadingRealTimeInventory: state.xProductList.isLoadingRealTimeInventory,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    loadRelatedProductsCarousel,
};

export interface ProductCarouselStyles {
    titleText?: TypographyPresentationProps;
    carouselWrapper?: InjectableCss;
    carouselItemWrapper?: InjectableCss;
    separator?: InjectableCss;
    cardWrapper?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    relatedCardWrapper?: InjectableCss;
    relatedCardsWrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const productCarouselStyles: ProductCarouselStyles = {
    titleText: {
        variant: "h3",
        forwardAs: "p",
        css: css`
            margin-bottom: 20px;
            padding-bottom: 4px;
            border-bottom: 1px solid ${color("common.border")};
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselItemWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            column-gap: 22px;
            row-gap: 20px;
        `,
    },
    separator: {
        css: css`
            width: 100%;
            height: 1px;
            border-bottom: 1px solid ${color("common.border")};
            margin-bottom: 20px;
        `,
    },
    cardWrapper: {
        css: css`
            width: calc(50% - 11px);
            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    relatedCardWrapper: {
        css: css`
            &:not(:last-child) {
                padding-bottom: 20px;
                border-bottom: 1px solid ${color("common.border")};
                margin-bottom: 20px;
            }
            ${({ isAuthenticated }: any) => {
                if (!isAuthenticated) {
                    return css`
                        &:not(:last-child) {
                            padding-bottom: 22px;
                            ${xScreen("lte", "md")} {
                                padding-bottom: 20px;
                            }
                        }
                    `;
                }
            }}
        `,
    },
};

const styles = productCarouselStyles;

const CustomRelatedProductsCarousel = ({
    id,
    fields,
    products,
    loadCarouselProducts,
    shellContext,
    session,
    isTabletScreen,
    variantChildren,
    variantTraits,
    loadRelatedProductsCarousel,
    errorMessage,
    parentProductId,
    isAuthenticated,
    productInfosById,
    hideItemsOutOfStock,
    isLoadingRealTimeInventory,
}: Props) => {
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const [productNumbersMap, setProductNumbersMap] = useState<SafeDictionary<string>>({});
    const shouldFilter = isAuthenticated && hideItemsOutOfStock;

    const isLoading = React.useMemo(() => {
        return Object.values(productInfosById || {}).some(
            product =>
            (isLoadingRealTimeInventory ||
                (!product?.pricing && !product?.failedToLoadPricing)),
        );
    }, [productInfosById, isLoadingRealTimeInventory]);

    const filteredProducts = React.useMemo(() => {
        if (!products || !productInfosById) {
            return [];
        }
        return products.filter(product => {
            const productInfo = productInfosById[product.id];
            const qtyOnHand = productInfo?.inventory?.qtyOnHand ?? 0;
            const unitNetPrice = productInfo?.pricing?.unitNetPrice ?? 0;
            return qtyOnHand > 0 && unitNetPrice > 0;
        });
    }, [products, productInfosById]);

    const finalProducts = shouldFilter ? filteredProducts : products;

    useEffect(() => {
        if (fields.relatedProductType === "CustomRelatedProducts") {
            return;
        }
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: "relatedProducts",
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: "",
            displayProductsFrom: "allCategories",
            selectedCategoryIds: [],
            numberOfProductsToDisplay: 10,
            widgetPosition: 1,
            isProductDetailsPage: true,
            productId: productContext?.product?.id,
            isProductListPage: false,
            category,
            isBrandDetailsPage: false,
            brand,
        });
    }, [fields.relatedProductType, productContext?.product?.id, category?.id, brand?.id, session?.pickUpWarehouse?.id]);

    useEffect(() => {
        if (fields.relatedProductType !== "CustomRelatedProducts") {
            return;
        }
        const productNumbers: string[] = [];
        const productNumbersMap = {} as SafeDictionary<string>;
        if (productContext?.product && productContext?.product.properties?.relatedProducts) {
            const relatedProductss = JSON.parse(
                productContext.product.properties.relatedProducts,
            ) as RelatedProductModel[];
            relatedProductss.forEach(related => {
                productNumbers.push(related.RelatedProductNumber);
                productNumbersMap[related.RelatedProductNumber] = related.RecommendedQty?.toString();
            });
        }
        if (variantChildren && variantChildren.length) {
            variantChildren.forEach((variant: ProductModel) => {
                if (!variant.properties || !variant.properties.relatedProducts) {
                    return;
                }
                const relatedProductss = JSON.parse(variant.properties.relatedProducts) as RelatedProductModel[];
                relatedProductss.forEach(related => {
                    productNumbers.push(related.RelatedProductNumber);
                    productNumbersMap[related.RelatedProductNumber] = related.RecommendedQty?.toString();
                });
            });
        }

        setProductNumbersMap(productNumbersMap);

        loadRelatedProductsCarousel({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            productId: parentProductId,
            productNumbers,
        });
    }, [fields.relatedProductType, productContext?.product?.id, variantChildren?.length]);

    if (isLoading && shouldFilter) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    // To prevent cache issue
    if (
        !finalProducts ||
        finalProducts.length === 0 ||
        (Object.keys(productNumbersMap).length === 0 && fields.relatedProductType === "CustomRelatedProducts") ||
        (finalProducts?.length === 0 && !isLoading && shouldFilter)
    ) {
        return null;
    }

    const showBrandBlocks = fields.showBrandName && products?.some(o => !!o.brand);

    return (
        <>
            {fields.relatedProductType !== "CustomRelatedProducts" ? (
                <StyledWrapper>
                    {fields.title && <Typography {...styles.titleText}>{fields.title}</Typography>}
                    <StyledWrapper {...styles.carouselWrapper}>
                        {products?.map((product, index) => {
                            if (index % 2) {
                                return;
                            }
                            return (
                                <StyledWrapper {...styles.carouselItemWrapper} key={product.id}>
                                    <StyledWrapper {...styles.cardWrapper}>
                                        <ProductCarouselProduct
                                            key={product.id}
                                            carouselId={id}
                                            product={product}
                                            showImage={fields.showImage}
                                            showBrand={showBrandBlocks || false}
                                            showTitle={fields.showTitle}
                                            showAvailability={fields.showAvailability}
                                            showImageBadges={fields.showImageBadges}
                                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                            showTextBadges={false}
                                            showPrice={false}
                                            showPartNumbers={false}
                                            showAddToCart={false}
                                            showAddToList={false}
                                            extendedStyles={styles.carouselProductStyles}
                                        />
                                    </StyledWrapper>
                                    {isTabletScreen && index + 1 !== products.length && (
                                        <StyledWrapper key={`separator_${index}`} {...styles.separator}></StyledWrapper>
                                    )}
                                    {index + 1 < products.length && (
                                        <StyledWrapper {...styles.cardWrapper}>
                                            <ProductCarouselProduct
                                                key={products[index + 1].id}
                                                carouselId={id}
                                                product={products[index + 1]}
                                                showImage={fields.showImage}
                                                showBrand={showBrandBlocks || false}
                                                showTitle={fields.showTitle}
                                                showAvailability={fields.showAvailability}
                                                showImageBadges={fields.showImageBadges}
                                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                                showTextBadges={false}
                                                showPrice={false}
                                                showPartNumbers={false}
                                                showAddToCart={false}
                                                showAddToList={false}
                                                extendedStyles={styles.carouselProductStyles}
                                            />
                                        </StyledWrapper>
                                    )}
                                    {index + 1 !== products.length && (
                                        <StyledWrapper key={`separator_${index}`} {...styles.separator}></StyledWrapper>
                                    )}
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <StyledWrapper>
                    {fields.title && <Typography {...styles.titleText}>{fields.title}</Typography>}
                    <StyledWrapper {...styles.relatedCardsWrapper}>
                        {finalProducts?.map((relatedProduct: ProductModel) => (
                            <StyledWrapper
                                {...styles.relatedCardWrapper}
                                {...{ isAuthenticated }}
                                key={`${relatedProduct.id}`}
                            >
                                <RelatedProductCard
                                    product={relatedProduct}
                                    recommendedQty={
                                        Number(productNumbersMap[relatedProduct.productNumber]) || undefined
                                    }
                                    showBrandName={fields.showBrandName}
                                    showAvailability={fields.showAvailability}
                                    showPrice={fields.showPrice}
                                    showAddToCart={fields.showAddToCart}
                                    showImageBadges={fields.showImageBadges}
                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                    carouselId={id}
                                />
                            </StyledWrapper>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withParentProductId(
        connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(CustomRelatedProductsCarousel))),
    ),
    definition: {
        group: "Common",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: item => item.fields[fields.relatedProductType] === "CustomRelatedProducts",
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
                isVisible: item => item.fields[fields.relatedProductType] === "CustomRelatedProducts",
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "3",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
